import { Platform } from 'react-native';

const STORAGE_PREFIX = 'tabbio:pending-nova-draft:v1:';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1_000;

export interface PendingNovaDraft {
  sentence: string;
  scheduleHint?: string;
  createdAt: number;
}

function createToken() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function storageKey(token: string) {
  return `${STORAGE_PREFIX}${token}`;
}

async function setValue(key: string, value: string) {
  if (Platform.OS === 'web') {
    window.localStorage.setItem(key, value);
    return;
  }
  const SecureStore = require('expo-secure-store') as typeof import('expo-secure-store');
  await SecureStore.setItemAsync(key, value);
}

async function getValue(key: string) {
  if (Platform.OS === 'web') {
    return window.localStorage.getItem(key);
  }
  const SecureStore = require('expo-secure-store') as typeof import('expo-secure-store');
  return SecureStore.getItemAsync(key);
}

async function deleteValue(key: string) {
  if (Platform.OS === 'web') {
    window.localStorage.removeItem(key);
    return;
  }
  const SecureStore = require('expo-secure-store') as typeof import('expo-secure-store');
  await SecureStore.deleteItemAsync(key);
}

export async function persistPendingNovaDraft(params: {
  sentence: string;
  scheduleHint?: string;
}) {
  const token = createToken();
  const draft: PendingNovaDraft = {
    sentence: params.sentence,
    scheduleHint: params.scheduleHint,
    createdAt: Date.now(),
  };
  await setValue(storageKey(token), JSON.stringify(draft));
  return token;
}

export async function consumePendingNovaDraft(token: string) {
  const key = storageKey(token);
  try {
    const raw = await getValue(key);
    await deleteValue(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingNovaDraft;
    if (
      typeof parsed.sentence !== 'string' ||
      typeof parsed.createdAt !== 'number' ||
      Date.now() - parsed.createdAt > MAX_AGE_MS
    ) {
      return null;
    }
    return parsed;
  } catch {
    await deleteValue(key).catch(() => undefined);
    return null;
  }
}
